import { useEffect, useRef } from 'react'
import { motion, AnimatePresence, useDragControls, type PanInfo } from 'motion/react'
import { SheetGrip } from '../SheetGrip'
import { X, Check, Lock, ChevronLeft } from 'lucide-react'
import { LEVEL_XP_REQUIREMENTS, LEVEL_CAP, TUCOIN_LEVEL_BONUSES } from '@tfa/shared'
import { getCharacterImage } from '../../assets/character'
import { TuCoinIcon } from '../TuCoinIcon'

const F = 'Roboto, sans-serif'

interface Props {
  open: boolean
  onClose: () => void
  currentLevel: number
  currentXp: number
  product: string | null
  gender?: string | null
}

interface PhaseDef {
  id: 'p1' | 'p2' | 'p3'
  name: string
  subtitle: string
  desc: string
  levels: number[]
  accent: string
  ring: string
  /** very light wash used as the card background for this phase */
  wash: string
}

// Границы совпадают с LEVEL_CAP / TUCOIN_LEVEL_BONUSES:
// ТФА 1 = 1–10, ТФА 2 = 11–17, ТФА 3 (Клуб) = 18–30.
const PHASES: PhaseDef[] = [
  {
    id: 'p1',
    name: 'ТФА 1',
    subtitle: 'Похудение',
    desc: 'Старт пути. Персонаж постепенно подтягивается, появляются первые рельефы и брендовая форма ТФА.',
    levels: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    accent: '#6C5CE7',
    ring: 'rgba(108,92,231,0.30)',
    wash: 'rgba(108,92,231,0.07)',
  },
  {
    id: 'p2',
    name: 'ТФА 2',
    subtitle: 'Спортивное',
    desc: 'Рост и трансформация. Тело становится более атлетичным, гардероб обновляется, появляются аксессуары.',
    levels: [11, 12, 13, 14, 15, 16, 17],
    accent: '#4A9FE5',
    ring: 'rgba(74,159,229,0.32)',
    wash: 'rgba(74,159,229,0.08)',
  },
  {
    id: 'p3',
    name: 'ТФА 3',
    subtitle: 'Клуб · Легенда',
    desc: 'Финальная стадия. Атлет в пике формы, доступны эксклюзивные образы Клуба и максимальные эффекты.',
    levels: [18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30],
    accent: '#00B894',
    ring: 'rgba(0,184,148,0.32)',
    wash: 'rgba(0,184,148,0.08)',
  },
]

function getProductCap(product: string | null): number {
  if (!product) return 30
  return LEVEL_CAP[product] ?? 30
}

function getXpRequired(level: number): number {
  return LEVEL_XP_REQUIREMENTS[level as keyof typeof LEVEL_XP_REQUIREMENTS] ?? 0
}

function getTuCoinReward(level: number): number {
  return TUCOIN_LEVEL_BONUSES[level] ?? 0
}

// Лейбл ступени по продукту. Для CLUB ступень-индикатор не показываем
// (клуб — вне структурной программы ТФА 1→3).
const TIER_LABEL: Record<string, string> = {
  TFA_STEP1: 'ТФА 1',
  TFA_STEP2: 'ТФА 2',
}

export function LevelsProgressionSheet({ open, onClose, currentLevel, currentXp, product, gender }: Props) {
  const dragControls = useDragControls()
  const cap = getProductCap(product)
  const tierLabel = product ? TIER_LABEL[product] : undefined
  const tierLevel = Math.min(currentLevel, cap)
  const scrollerRef = useRef<HTMLDivElement>(null)
  const currentCardRef = useRef<HTMLDivElement>(null)

  const handleDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.y > 80) onClose()
  }

  // Auto-scroll the horizontal strip to the current level card when the sheet opens.
  useEffect(() => {
    if (!open) return
    const t = setTimeout(() => {
      const el = currentCardRef.current
      const scroller = scrollerRef.current
      if (el && scroller) {
        const left = el.offsetLeft - 16
        scroller.scrollTo({ left: left < 0 ? 0 : left, behavior: 'smooth' })
      }
    }, 220)
    return () => clearTimeout(t)
  }, [open])

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0, zIndex: 410,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
            fontFamily: F,
          }}
        >
          <motion.div
            data-testid="levels-progression-sheet"
            initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            drag="y" dragListener={false} dragControls={dragControls}
            dragConstraints={{ top: 0 }} dragElastic={0.1}
            onDragEnd={handleDragEnd}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#F5F6FA',
              borderTopLeftRadius: 28, borderTopRightRadius: 28,
              paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
              maxHeight: '94vh',
              display: 'flex', flexDirection: 'column',
            }}
          >
            <div
              onPointerDown={(e) => dragControls.start(e)}
              style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, paddingBottom: 4, cursor: 'grab', touchAction: 'none' }}
            >
              <SheetGrip onClose={onClose} />
            </div>

            <div style={{ position: 'relative', padding: '6px 20px 8px' }}>
              {/* #103: кнопка-стрелка назад «как на профиле» — единый способ выйти */}
              <button
                data-testid="levels-progression-back"
                onClick={onClose}
                aria-label="Назад"
                style={{
                  position: 'absolute', top: 4, left: 12,
                  width: 32, height: 32, borderRadius: '50%',
                  background: 'rgba(0,0,0,0.06)', border: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <ChevronLeft size={18} color="#6B7B8D" />
              </button>
              <button
                data-testid="levels-progression-close"
                onClick={onClose}
                aria-label="Закрыть"
                style={{
                  position: 'absolute', top: 4, right: 16,
                  width: 32, height: 32, borderRadius: '50%',
                  background: 'rgba(0,0,0,0.06)', border: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <X size={16} color="#6B7B8D" />
              </button>

              <div style={{ paddingLeft: 40, paddingRight: 40 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#9AA5B4', letterSpacing: 1, textTransform: 'uppercase' }}>
                    Прогресс уровней
                  </div>
                  {tierLabel && (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      fontSize: 11, fontWeight: 800,
                      background: 'rgba(108,92,231,0.12)', color: '#6C5CE7',
                      border: '1px solid rgba(108,92,231,0.28)',
                      borderRadius: 8, padding: '2px 8px',
                    }}>
                      {tierLabel} · {tierLevel} из {cap}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 2 }}>
                  <span style={{ fontSize: 30, fontWeight: 900, color: '#1C2D41', lineHeight: 1 }}>{currentLevel}</span>
                  <span style={{ fontSize: 13, color: '#6B7B8D' }}>уровень · {currentXp.toLocaleString('ru-RU')} XP</span>
                </div>
                <div style={{ fontSize: 11, color: '#6B7B8D', marginTop: 2 }}>
                  Лимит уровня для продукта: <b style={{ color: '#1C2D41' }}>{cap}</b>. XP сверх лимита банкуется. Листай вправо →
                </div>
              </div>
            </div>

            {/* Horizontal strip: phase intro panels + large per-level cards */}
            <div
              ref={scrollerRef}
              style={{
                display: 'flex',
                gap: 12,
                overflowX: 'auto',
                overflowY: 'hidden',
                padding: '4px 20px 14px',
                scrollSnapType: 'x mandatory',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              {PHASES.map((phase) => {
                const phaseStart = phase.levels[0]
                const phaseEnd = phase.levels[phase.levels.length - 1]
                const isCurrentPhase = currentLevel >= phaseStart && currentLevel <= phaseEnd
                const phaseLocked = phaseStart > cap
                return [
                  // --- Phase intro panel (acts as the colored divider between tiers) ---
                  <div
                    key={`${phase.id}-intro`}
                    style={{
                      flex: '0 0 auto',
                      width: 150,
                      scrollSnapAlign: 'start',
                      borderRadius: 20,
                      padding: 16,
                      background: `linear-gradient(160deg, ${phase.accent}, ${phase.accent}cc)`,
                      color: 'white',
                      display: 'flex', flexDirection: 'column',
                      boxShadow: `0 8px 22px ${phase.ring}`,
                      opacity: phaseLocked ? 0.78 : 1,
                    }}
                  >
                    <div style={{
                      alignSelf: 'flex-start',
                      fontSize: 11, fontWeight: 800,
                      background: 'rgba(255,255,255,0.22)',
                      padding: '3px 9px', borderRadius: 7,
                      letterSpacing: 0.5,
                    }}>
                      {phaseStart}–{phaseEnd}
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 900, marginTop: 10, lineHeight: 1.05 }}>{phase.name}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, opacity: 0.9, marginTop: 1 }}>{phase.subtitle}</div>
                    <p style={{ fontSize: 11, lineHeight: 1.45, margin: '10px 0 0', opacity: 0.92, flex: 1 }}>
                      {phase.desc}
                    </p>
                    <div style={{
                      marginTop: 10,
                      alignSelf: 'flex-start',
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5,
                      background: 'rgba(255,255,255,0.22)',
                      padding: '4px 9px', borderRadius: 8,
                    }}>
                      {isCurrentPhase ? 'Текущий этап' : phaseLocked ? <><Lock size={10} /> По продукту</> : 'Этап'}
                    </div>
                  </div>,

                  // --- Large per-level cards ---
                  ...phase.levels.map((lvl) => {
                    const xpRequired = getXpRequired(lvl)
                    const reached = currentLevel >= lvl
                    const isCurrent = currentLevel === lvl
                    const imgSrc = getCharacterImage(lvl, gender as never)
                    const lvlLocked = lvl > cap
                    const tucoin = getTuCoinReward(lvl)
                    const xpToUnlock = Math.max(0, xpRequired - currentXp)
                    return (
                      <div
                        key={lvl}
                        ref={isCurrent ? currentCardRef : undefined}
                        style={{
                          flex: '0 0 auto',
                          width: 224,
                          scrollSnapAlign: 'start',
                          borderRadius: 20,
                          padding: 14,
                          background: isCurrent
                            ? `linear-gradient(160deg, ${phase.accent}22, ${phase.accent}08)`
                            : phase.wash,
                          border: isCurrent
                            ? `2px solid ${phase.accent}`
                            : reached ? '1.5px solid rgba(52,199,89,0.35)' : '1px solid rgba(26,58,92,0.08)',
                          position: 'relative',
                          opacity: lvlLocked && !isCurrent ? 0.62 : 1,
                          display: 'flex', flexDirection: 'column',
                        }}
                      >
                        {/* status pill top-left */}
                        <div style={{
                          position: 'absolute', top: 12, left: 12, zIndex: 2,
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          fontSize: 10, fontWeight: 800,
                          padding: '4px 9px', borderRadius: 8,
                          textTransform: 'uppercase', letterSpacing: 0.4,
                          color: isCurrent ? 'white' : reached ? '#1F9B4E' : '#6B7B8D',
                          background: isCurrent ? phase.accent : reached ? 'rgba(52,199,89,0.15)' : 'rgba(26,58,92,0.07)',
                        }}>
                          {isCurrent ? 'Текущий' : reached ? <><Check size={11} strokeWidth={3} /> Открыт</> : <><Lock size={10} /> Закрыт</>}
                        </div>

                        {/* Big character image */}
                        <div style={{
                          width: '100%', aspectRatio: '3 / 4',
                          borderRadius: 16,
                          background: 'rgba(255,255,255,0.65)',
                          overflow: 'hidden',
                          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
                          marginTop: 20, marginBottom: 10,
                        }}>
                          {imgSrc ? (
                            <img
                              src={imgSrc}
                              alt={`Уровень ${lvl}`}
                              // 30 кадров в горизонтальной ленте: грузим только видимые.
                              loading="lazy"
                              decoding="async"
                              style={{
                                width: '100%', height: '100%', objectFit: 'contain',
                                filter: reached || isCurrent ? 'none' : 'grayscale(60%) opacity(0.7)',
                              }}
                            />
                          ) : (
                            <span style={{ fontSize: 48, fontWeight: 900, color: phase.accent, alignSelf: 'center' }}>{lvl}</span>
                          )}
                        </div>

                        <div style={{ fontSize: 17, fontWeight: 900, color: isCurrent ? phase.accent : '#1C2D41' }}>
                          Уровень {lvl}
                        </div>

                        {/* XP line */}
                        <div style={{ fontSize: 12, color: '#6B7B8D', marginTop: 3 }}>
                          {reached ? (
                            <>Порог: <b style={{ color: '#1C2D41' }}>{xpRequired.toLocaleString('ru-RU')} XP</b></>
                          ) : (
                            <>До открытия: <b style={{ color: phase.accent }}>{xpToUnlock.toLocaleString('ru-RU')} XP</b></>
                          )}
                        </div>

                        {/* TuCoin line — монетка вместо слова «TuCoin» */}
                        <div style={{ fontSize: 11, color: '#9AA5B4', marginTop: 2 }}>
                          {tucoin > 0
                            ? <>За открытие: <b style={{ color: '#8A6D00', display: 'inline-flex', alignItems: 'center', gap: 3 }}>+{tucoin} <TuCoinIcon size={12} /></b></>
                            : 'Стартовый уровень'}
                        </div>
                      </div>
                    )
                  }),
                ]
              })}
            </div>

            {/* Note */}
            <div style={{ padding: '0 20px' }}>
              <div style={{
                background: 'rgba(108,92,231,0.06)',
                border: '1px solid rgba(108,92,231,0.18)',
                borderRadius: 14, padding: 12,
              }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: '#1C2D41', marginBottom: 4 }}>
                  Как это работает
                </div>
                <p style={{ fontSize: 11, color: '#3D4A5C', margin: 0, lineHeight: 1.5 }}>
                  Опыт (XP) копится за тренировки, отчёты по питанию, замеры, фото и просмотр лекций.
                  На каждом уровне меняется образ персонажа и начисляются TuCoin. После лимита продукта XP
                  продолжает копиться — при апгрейде на следующий продукт он сразу применится и поднимет уровень.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
